import 'firebase/app'
import firebase from 'firebase'
import 'firebase/database'
import { FormikErrors } from 'formik'
import ky from 'ky'
import _ from 'lodash'
import moment from 'moment'
import { useMoveMenuPage } from '../../containers/App/operations'
import { Fields as CreateDeviceFields } from '../../containers/DeviceForm/CreateDeviceForm'
import { locale } from '../../locale'
import { createFirebase, initializeFirebase } from '../../services/firebase'
import {
  Category,
  Device,
  DeviceRaw,
  Log,
  LogRaw,
  ThunkAction,
  UpdateDeviceField,
  UpdateUserField,
  UpdateYuatsuDeviceField,
  User,
} from '../../types'
import { genPassword, genToken, toLog, unix } from '../../utils'
import { encrypt } from '../../utils/encrypt'
import * as authActions from '../Auth/actions'
import { saveAdminLogin } from '../Auth/operations'
import { getLoginUser, getLoginUserDeviceIds } from '../Auth/selectors'
import {
  receiveDevice,
  receiveDeviceToken,
  removeDevice,
  updateDevice,
} from '../DeviceById/actions'
// import * as actions from './actions'
import { saveDevice } from '../DeviceById/operations'
import { resetLoginForm, setLoginErrorMessage } from '../LoginForm/actions'
import {
  createDeviceEnd,
  createUserEnd,
  editDeviceEnd,
  editUserEnd,
} from '../SystemCache/actions'
import { receiveUser, removeUser } from '../UserById/actions'

const { REACT_APP_API_HOST } = process.env

const fapp = initializeFirebase()
const fdb = fapp.database()
const messageLoginError = setLoginErrorMessage(
  '認証できませんでした。ご登録いただいているメールアドレスとパスワードを入力してください。',
)

export function passwordLogin(email: string, password: string) {
  return async (dispatch) => {
    console.log('passwordLogin')
    await fapp
      .auth()
      .signInWithEmailAndPassword(email, password)
      .catch((_e) => {
        dispatch(messageLoginError)
        return { user: null }
      })
  }
}

export function logout(): ThunkAction {
  return async (dispatch) => {
    await fapp.auth().signOut().catch(console.error)
    await dispatch(authActions.logout())
  }
}

function authStateChanged(user: firebase.User | null, redirect: boolean) {
  return async (dispatch) => {
    if (!user) {
      return dispatch(authActions.loginFailed())
    }
    const adminUserRef = fdb.ref(`admin-user/${user.uid}`)
    const adminSnapshot = await adminUserRef.once('value')

    if (adminSnapshot.exists()) {
      const user = adminSnapshot.val()

      await dispatch(saveAdminLogin(user))
      await dispatch(resetLoginForm())
      if (redirect) {
        useMoveMenuPage()
      }
    } else {
      dispatch(messageLoginError)
      dispatch(authActions.loginFailed())
    }
  }
}

export function refInit(): ThunkAction {
  return async (dispatch) => {
    fapp.auth().onAuthStateChanged((user) => {
      dispatch(authStateChanged(user, false))
    })
  }
}

export function initializeAdmin(): ThunkAction {
  return async (dispatch) => {
    dispatch(syncUserData())
    dispatch(syncDeviceData())
    dispatch(syncDeviceToken())
  }
}

const userSnapshotChanged = (snapshot) => (dispatch) => {
  if (!snapshot) {
    return
  }
  const user = snapshot.val()

  if (!user) {
    return
  }
  dispatch(receiveUser(user))
}

export function syncUserData(): ThunkAction {
  return async (dispatch) => {
    fdb
      .ref('user')
      .orderByChild('deleted')
      .equalTo(false)
      .on('value', (snapshot) => {
        if (!snapshot) {
          return
        }
        const users = snapshot.val() as { [userId: string]: User }

        _.sortBy(Object.values(users), (u) => u.company).forEach((user) => {
          dispatch(receiveUser(user))
        })
      })
  }
}

export function syncLoginUserData(): ThunkAction {
  return async (dispatch, getState) => {
    const userState = getLoginUser(getState())

    if (!userState) {
      return
    }

    fdb.ref(`user/${userState.id}`).on('value', (snapshot) => {
      dispatch(userSnapshotChanged(snapshot))
    })
  }
}

export function syncDeviceData(): ThunkAction {
  return async (dispatch) => {
    fdb
      .ref('device')
      .orderByChild('deleted')
      .equalTo(false)
      .on('value', (snapshot) => {
        if (!snapshot) {
          return
        }
        const devices = snapshot.val()

        _.map(devices, (device, id) => {
          dispatch(receiveDevice({ ...device, id }))
        })
      })
  }
}

export function syncDeviceToken(): ThunkAction {
  return async (dispatch) => {
    fdb.ref('device-token').on('value', (snapshot) => {
      if (!snapshot) {
        return
      }
      const tokens: { [id: string]: { token: string } } = snapshot.val()

      _.map(tokens, (doc, id) => {
        dispatch(receiveDeviceToken({ id, token: doc.token }))
      })
    })
  }
}

export function loadDevices(): ThunkAction {
  return async (dispatch, getState) => {
    dispatch(syncLoginUserData())
    const deviceIds = getLoginUserDeviceIds(getState())

    deviceIds.forEach(async (deviceId) => {
      const { deviceRef, sessionRef } = await getSessionRefs(deviceId)
      const deviceLastLogRef = sessionRef.child('lastLog')
      // const deviceLogCountRef = sessionRef.child('counts')

      deviceRef.on('value', (snapshot) => {
        if (!snapshot) {
          return
        }
        const device = snapshot.val()

        dispatch(
          updateDevice({
            id: deviceId,
            data: device.data,
            info: device.info,
            yuatsu: device.yuatsu,
            updatedAt: device.updatedAt,
          }),
        )
      })

      deviceLastLogRef.on('value', async (snapshot, _id) => {
        const device = (await deviceRef.once('value')).val()

        if (!snapshot) {
          return
        }
        const val = snapshot.val()

        if (!val) {
          return
        }
        const logRaw: LogRaw = val

        console.log('receive firebase', logRaw)

        const log = toLog(logRaw)

        dispatch(
          saveDevice({
            ..._.pick(device, [
              'data',
              'category',
              'info',
              'yuatsu',
              'updatedAt',
              'currentUserId',
              'currentSessionId',
            ]),
            id: deviceId,
            lastLog: log,
          }),
        )
      })

      // const logsSnap = await deviceLogRef
      //   .orderByChild('timestamp')
      //   .startAt(dayStart)
      //   .endAt(dayEnd)
      //   .once('value')
      // dispatch(saveDayLogs(deviceId, day, logsSnap.val()))
    })
  }
}

export async function getSessionRefs(deviceId: string) {
  const deviceRef = fdb.ref(`device/${deviceId}`)
  const deviceRaw: DeviceRaw = (await deviceRef.once('value')).val()
  const { currentUserId, currentSessionId } = deviceRaw
  const sessionRef = fdb.ref(
    `user-device-session-log/${currentUserId}/${deviceId}/${currentSessionId}`,
  )

  return { deviceRef, deviceRaw, sessionRef }
}

const dayEndsUnix = (day: string) => {
  const m = moment(day, 'YYYY-MM-DD')
  const dayStart = m.startOf('day').unix() * 1000
  const dayEnd = m.endOf('day').unix() * 1000

  return { dayStart, dayEnd }
}

export async function loadLogs(deviceId: string, day: string): Promise<Log[]> {
  const { dayStart, dayEnd } = dayEndsUnix(day)
  const { sessionRef } = await getSessionRefs(deviceId)

  const deviceLogRef = sessionRef.child('logs')

  const logsSnap = await deviceLogRef
    .orderByChild('timestamp')
    .startAt(dayStart)
    .endAt(dayEnd)
    .once('value')

  const logRaws: { [key: string]: LogRaw } = logsSnap.val()
  const logs: Log[] = _.map(logRaws, (log, key) => toLog(log, key))

  return logs
}

export function updateUserTitle(userId: string, title: string) {
  return () => {
    fdb.ref(`user/${userId}/title`).set(title)
  }
}

export async function updateDeviceCurrentUser(
  deviceId: string,
  newUid: string,
) {
  const deviceRef = fdb.ref(`device/${deviceId}`)
  const device = (await deviceRef.once('value')).val() as Device
  const newCurrentSessionId = Number(device.currentSessionId) + 1

  const oldSid = device.currentSessionId
  const oldUid = device.currentUserId

  if (newUid === oldUid) {
    return
  }

  if (oldUid) {
    if (device.category === 'yuatsu') {
      const user = (await fdb.ref(`user/${oldUid}`).once('value')).val() as User

      console.log({ user })

      if (user.yuatsu) {
        const cells = { ...user.yuatsu.cells }

        Object.entries(user.yuatsu?.cells || {}).forEach(([key, val]) => {
          if (val === deviceId) {
            delete cells[key]
          }
        })
        await fdb.ref(`user/${oldUid}/yuatsu/cells`).set(cells)
      }
    }
    await fdb.ref(`user/${oldUid}/deviceIds/${deviceId}`).set(null)
    if (device.info.threasholds) {
      await fdb
        .ref(`user-device-session/${oldUid}/${deviceId}/${oldSid}`)
        .update({
          threasholds: device.info.threasholds,
          category: device.category,
        })
    }
  }
  if (newUid) {
    await fdb.ref(`user/${newUid}/deviceIds/${deviceId}`).set(true)

    await deviceRef.update({
      currentUserId: newUid,
      currentSessionId: newCurrentSessionId,
    })
    await fdb
      .ref(`user-device-session/${newUid}/${deviceId}/${newCurrentSessionId}`)
      .set({
        createdAt: unix(),
        category: device.category,
      })

    await deviceRef.child('info').update({
      status: 'stop',
    })
  }
}

export async function updateDeviceInfo(
  deviceId: string,
  fields: UpdateDeviceField,
) {
  await fdb
    .ref(`device/${deviceId}/info`)
    .update(_.pick(fields, ['label', 'memo']))
  await updateDeviceCurrentUser(deviceId, fields.currentUserId)
}

export async function updateYuatsuDeviceInfo(
  deviceId: string,
  { label, memo, imei, currentUserId }: UpdateYuatsuDeviceField,
) {
  await fdb.ref(`device/${deviceId}/info`).update({ label, memo })
  await fdb.ref(`device/${deviceId}/yuatsu`).update({ imei: imei.trim() })
  await updateDeviceCurrentUser(deviceId, currentUserId)
}

// device-token はデバイス認証の credential なので Math.random() は使えない。
// V8 の Math.random() は xorshift128+ で、少数の出力から内部状態を復元して
// 過去・未来の値を予測できる。加えて toString(16) の桁数が値によって変動し、
// 個体ごとにトークン長がばらつく問題もあった。
const randHex64 = () => {
  const bytes = new Uint8Array(32)

  crypto.getRandomValues(bytes)
  // スプレッドではなく Array.from を使う。このリポジトリの tsconfig は
  // downlevelIteration が無効で、Uint8Array のスプレッドがコンパイルを通らない
  return Array.from(bytes)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')
}

async function createDeviceFdb(id: string, category: Category) {
  const snap = await fdb.ref(`device/${id}`).once('value')

  if (snap.exists() && !(snap.val() as DeviceRaw).deleted) {
    throw new Error('同じ管理Noが存在します。')
  }
  fdb.ref(`device-token`).update({ [id]: { token: randHex64() } })
  fdb.ref(`device/${id}`).set({
    category,
    currentSessionId: 0,
    currentUserId: 'none',
    status: 'stop',
    visible: true,
    deleted: false,
  })
}

export async function resetPasswordRequest(email: string) {
  const ok = window.confirm(`${email} にパスワード再設定リンクを送りますか？`)

  if (ok) {
    await fapp.auth().sendPasswordResetEmail(email)
    alert(`${email} にパスワード再設定用リンクを送信しました。ご確認ください。`)
  }
}

export function createDevice(
  fields: CreateDeviceFields,
  setErrors: (errors: FormikErrors<{ id: string }>) => void,
): ThunkAction {
  return (dispatch) => {
    createDeviceFdb(fields.id, fields.category)
      .then(() => {
        dispatch(createDeviceEnd())
      })
      .catch((e) => {
        setErrors({ id: e.message })
      })
  }
}

export function deleteDevice(id: string): ThunkAction {
  return async (dispatch) => {
    const result = window.confirm(
      `${locale.deviceEntity} ${id} を削除しますか？`,
    )

    if (!result) {
      return
    }
    const device = await fdb.ref(`device/${id}`).once('value')

    if (!device.exists()) {
      throw Error('no device')
    }
    const userId = (device.val() as DeviceRaw).currentUserId
    const userRef = fdb.ref(`user/${userId}`)
    const snap = await userRef.once('value')
    const { deviceIds } = snap.val() as User

    delete deviceIds[id]

    await userRef.update({ deviceIds, updatedAt: unix() })

    fdb.ref(`device/${id}`).update({ deleted: true })
    fdb.ref(`device-token/${id}`).remove()
    dispatch(removeDevice({ id }))
    dispatch(editDeviceEnd())
  }
}

type AuthError = {
  error: true
  message: string
}

const isError = (
  res: false | firebase.auth.UserCredential | AuthError,
): res is AuthError => {
  return res && res['error'] && typeof res['message'] === 'string'
}

async function getUserByEmail(
  email: string,
): Promise<{ error: true; message: string } | { error: false; id: string }> {
  const usersSs = await fdb
    .ref(`user`)
    .orderByChild('email')
    .equalTo(email)
    .once('value')
  const users = usersSs.val()

  if (_.values(users).length === 1) {
    // re-use user
    const user = _.values(users)[0]

    if (!user.deleted) {
      return {
        error: true,
        message: '同じメールアドレスが存在します。',
      }
    }
    // return false
    const id = _.keys(users)[0]

    return { id, error: false }
  }
  // create new user
  const productionPassword = genPassword()
  const secondFirebase = createFirebase('second')
  const res = await secondFirebase
    .auth()
    .createUserWithEmailAndPassword(email, productionPassword)
    .catch((e): AuthError | false => {
      console.log({ e })
      if (e.code === 'auth/email-already-in-use') {
        // return false
        return {
          error: true,
          message:
            '作成できませんでした。メールアドレスが管理ユーザに使われています。',
        }
      }
      return false
    })

  await secondFirebase.delete()
  if (isError(res)) {
    return { error: true, message: res.message }
  }
  if (!res || !res.user) {
    return { error: true, message: '作成できませんでした。' }
  }
  const { user } = res

  await fdb.ref(`user-production-password/${user.uid}`).set({
    password: encrypt(productionPassword),
  })
  return { id: user.uid, error: false }
}

export function createUser(
  email: string,
  password: string,
  setErrors: (
    error: FormikErrors<{
      email: string
      password: string
    }>,
  ) => void,
) {
  return async (dispatch) => {
    const res = await getUserByEmail(email)

    if (res.error) {
      // error
      setErrors({ email: res.message })
      return
    }
    const { id } = res
    const userLastNidRef = fdb.ref(`counter/userLastNid`)
    const lastIdVal = (await userLastNidRef.once('value')).val()

    if (typeof lastIdVal !== 'number') throw Error('invalid lastId val')
    const nid = lastIdVal + 1

    const user: User = {
      id,
      nid,
      email,
      label: 'ゲスト',
      title: '現場',
      company: '未設定',
      createdAt: unix(),
      updatedAt: unix(),
      memo: '',
      deleted: false,
      deviceIds: {},
      allowIntegration: false,
      allowDownload: false,
      allowSurfaceTemp: true,
      allowDialGauge: false,
      allowMailNotify: false,
      alertMails: [],
      tokenBase64: btoa(genToken(32)),
      managedPassword: encrypt(password),
    }

    await fdb.ref(`user/${id}`).set(user)
    await userLastNidRef.set(nid)

    dispatch(createUserEnd())
  }
}

export function updateUser(
  id: string,
  fields: UpdateUserField,
  newMails: string[],
  originMail: string,
) {
  return async (dispatch) => {
    await fdb.ref(`user/${id}`).update({
      ..._.pick(fields, [
        'label',
        'memo',
        'allowIntegration',
        'allowDialGauge',
        'allowMailNotify',
        'allowSurfaceTemp',
        'alertMails',
        'allowDownload',
        'contactStartDate',
        'contactEndDate',
        'company',
      ]),
      managedPassword: encrypt(fields.password),
      updatedAt: unix(),
    })
    if (fields.allowMailNotify && newMails.length > 0) {
      // TODO: test mail api
      testMail(newMails, originMail)
    }

    dispatch(editUserEnd())
  }
}

export function deleteUser(id: string, name: string): ThunkAction {
  return async (dispatch) => {
    const result = window.confirm(
      `${locale.clientEntity}${name}を削除しますか？`,
    )

    if (result) {
      const userRef = fdb.ref(`user/${id}`)
      const deviceIds =
        (await userRef.child('deviceIds').once('value')).val() || {}

      await userRef.update({
        deleted: true,
        deviceIds: {},
        updatedAt: unix(),
      })
      await Promise.all(
        _.keys(deviceIds).map((deviceId) =>
          fdb.ref(`device/${deviceId}`).update({
            currentUserId: 'none',
          }),
        ),
      )
      dispatch(removeUser({ id }))
      dispatch(editUserEnd())
    }
  }
}

export async function getLastLog(device: Device) {
  const path = `user-device-session-log/${device.currentUserId}/${device.id}/${device.currentSessionId}/lastLog`

  const logsSnap = await fdb.ref(path).once('value')

  return logsSnap.exists() ? (logsSnap.val() as LogRaw) : null
}

async function testMail(newMails: string[], origin: string) {
  await ky
    .post((REACT_APP_API_HOST || '') + '/yuatsu_test_mail', {
      json: { newMails, origin },
    })
    .catch(console.error)
}
